import { app } from "../config/firebaseConfig";
import { getFunctions, httpsCallable } from "firebase/functions";

// Espelha o padrão de cache em 2 camadas de videoApi.js/avatarSpeechApi.js
// (Map em memória + sessionStorage, TTL de 5 minutos): as fotos de boca do
// avatar barato (viseme) não mudam durante uma sessão, então uma vez
// resolvidas as URLs assinadas não precisam ser buscadas de novo.
const CACHE_TTL_MS = 5 * 60 * 1000;
const inMemoryCache = new Map();

const readCache = (cacheKey) => {
  const now = Date.now();
  const mem = inMemoryCache.get(cacheKey);
  if (mem && mem.expiresAt > now) return mem.value;
  if (mem) inMemoryCache.delete(cacheKey);

  try {
    const raw = sessionStorage.getItem(`eia_mouth_assets:${cacheKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.expiresAt || parsed.expiresAt <= now) {
      sessionStorage.removeItem(`eia_mouth_assets:${cacheKey}`);
      return null;
    }
    inMemoryCache.set(cacheKey, parsed);
    return parsed.value || null;
  } catch {
    return null;
  }
};

const writeCache = (cacheKey, value) => {
  const payload = { value, expiresAt: Date.now() + CACHE_TTL_MS };
  inMemoryCache.set(cacheKey, payload);
  try {
    sessionStorage.setItem(`eia_mouth_assets:${cacheKey}`, JSON.stringify(payload));
  } catch {
    // ignora limite de storage
  }
};

/**
 * Resolve o acervo de imagens de boca (viseme) de um avatar em URLs assinadas
 * de leitura, prontas pra usar como `mouthAssets` do AvatarVisemePlayer:
 * { base, patchRect, closed, closedNarrow, consonant, l, fv, smile, openA,
 *   roundedO, roundedU }.
 *
 * @param {Object} opts
 * @param {string} opts.mouthAssetsPath - prefixo no Storage, ex:
 *   "eia/media/pesquisa-de-compliance/senhoritaCompliance/visemes".
 * @param {{left:number, top:number, width:number, height:number}} opts.patchRect
 * @returns {Promise<Object|null>} null se o acervo ainda não existe no Storage.
 */
export async function getMouthAssets({ mouthAssetsPath, patchRect }) {
  if (!mouthAssetsPath) return null;

  const cached = readCache(mouthAssetsPath);
  if (cached) return { ...cached, patchRect };

  const functions = getFunctions(app, "southamerica-east1");
  const callable = httpsCallable(functions, "getSignedMouthAssetUrls");
  const { data } = await callable({ path: mouthAssetsPath });
  const urls = data?.urls || {};

  // Sem "base" ou "closed" o player não tem o que mostrar — trata como
  // acervo ainda não publicado nesse Storage em vez de um objeto quebrado.
  if (!urls.base || !urls.closed) return null;

  writeCache(mouthAssetsPath, urls);
  return { ...urls, patchRect };
}
